import React, { useCallback, useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Modal,
  Form,
  Tabs,
  Tab,
  Accordion,
  Alert,
} from "react-bootstrap";
import { FaArrowLeft, FaPlus, FaTrash, FaSave, FaEraser } from "react-icons/fa";
import { AuthContext } from "../../context/AuthContext";
import DeleteModal from "../../components/modals/DeleteModal";
import SuccessModal from "../../components/modals/SuccessModal";
import { useNotificationModal } from "../../hooks/useNotificationModal";
import Spinner from "../../components/Spinner";
import "../../styles/Style.css";

import ColorManagement from "../../components/style_components/ColorManagement";
import SizeManagement from "../../components/style_components/SizeManagement";
import MaterialManagement from "../../components/style_components/MaterialManagement";
import FitManagement from "../../components/style_components/FitManagement";

import SizeGuideModal from "../../components/modals/SizeGuideModal";
import MeasureGuideModal from "../../components/modals/MeasureGuideModal";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

export default function StyleAttributes() {
  const params = useParams();
  const styleNumber = Object.values(params)[0];

  const navigate = useNavigate();
  const { userData } = useContext(AuthContext);

  const [style, setStyle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const {
    showSuccessModal,
    successMessage,
    successTitle,
    showErrorModal,
    errorMessage,
    errorTitle,
    showSuccess,
    showError,
    hideSuccess,
    hideError,
  } = useNotificationModal();

  const [allColors, setAllColors] = useState([]);
  const [allSizeRanges, setAllSizeRanges] = useState([]);
  const [allSizeRangesWithSizes, setAllSizeRangesWithSizes] = useState([]); // [{range, sizes:[]}]
  const [selectedSizeRangeId, setSelectedSizeRangeId] = useState("");
  const [assignedSizeRanges, setAssignedSizeRanges] = useState([]); // [{range, sizes:[]}]
  const [assignedRangeSizes, setAssignedRangeSizes] = useState({}); // {rangeId: [sizes]}
  const [allMaterials, setAllMaterials] = useState([]);
  const [allFits, setAllFits] = useState([]);

  const [styleColors, setStyleColors] = useState([]);
  const [styleSizes, setStyleSizes] = useState([]);
  const [styleMaterials, setStyleMaterials] = useState([]);
  const [styleFits, setStyleFits] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);

  // Delete modal state for each attribute type
  const [deleteModalInfo, setDeleteModalInfo] = useState({
    type: "",
    id: null,
  });

  // Size Guide Modal state
  const [showSizeGuideModal, setShowSizeGuideModal] = useState(false);
  // Measure Guide Modal state
  const [showMeasureGuideModal, setShowMeasureGuideModal] = useState(false);

  const [activeTab, setActiveTab] = useState("colors");
  const [attributeToggles, setAttributeToggles] = useState({
    colors: true,
    sizes: true,
    materials: true,
    fits: true,
  });
  const company_code = userData?.company_code;

  // Fetch company settings to determine which attribute types are enabled
  const fetchCompanySettings = useCallback(async () => {
    if (!company_code) return;
    try {
      const res = await fetch(
        `${BASE_URL}/api/admin/companies-by-code/${company_code}`,
      );
      if (!res.ok) return;
      const data = await res.json();
      const company = data.company || {};
      setAttributeToggles({
        colors:
          company.enable_colors === undefined ? true : !!company.enable_colors,
        sizes:
          company.enable_sizes === undefined ? true : !!company.enable_sizes,
        materials:
          company.enable_materials === undefined
            ? true
            : !!company.enable_materials,
        fits: company.enable_fits === undefined ? true : !!company.enable_fits,
      });
    } catch (err) {
      // ignore errors here; default to enabled
      console.error("Error fetching company settings", err);
    }
  }, [company_code]);

  // Fetch style details
  const fetchStyleDetails = useCallback(async () => {
    try {
      const response = await fetch(
        `${BASE_URL}/api/admin/styles/get-style/${styleNumber}?company_code=${company_code}`,
      );
      const data = await response.json();
      if (data.success) {
        setStyle(data.style);
      } else {
        showError("Product not found", "Error");
      }
    } catch (err) {
      showError(err.message || "Error fetching product details", "Error");
    }
  }, [styleNumber, company_code, showError]);

  // Fetch all available attributes
  const fetchAllAttributes = useCallback(async () => {
    try {
      const [colorsRes, sizeRangesRes, materialsRes, fitsRes] =
        await Promise.all([
          fetch(
            `${BASE_URL}/api/admin/colors/get-colors?company_code=${company_code}`,
          ),
          fetch(
            `${BASE_URL}/api/admin/sizes/get-size-ranges?company_code=${company_code}`,
          ),
          fetch(
            `${BASE_URL}/api/admin/materials/get-materials?company_code=${company_code}`,
          ),
          fetch(
            `${BASE_URL}/api/admin/fits/get-fits?company_code=${company_code}`,
          ),
        ]);

      const [colorsData, sizeRangesData, materialsData, fitsData] =
        await Promise.all([
          colorsRes.json(),
          sizeRangesRes.json(),
          materialsRes.json(),
          fitsRes.json(),
        ]);

      if (colorsData.success) setAllColors(colorsData.colors);
      if (sizeRangesData.success) {
        setAllSizeRanges(sizeRangesData.size_ranges);
        // Fetch sizes for each size range
        await fetchSizesForAllRanges(sizeRangesData.size_ranges);
      }
      if (materialsData.success) setAllMaterials(materialsData.materials);
      if (fitsData.success) setAllFits(fitsData.fits);
    } catch (err) {
      showError(err.message || "Error fetching attributes", "Error");
    }
  }, [company_code, showError]);

  // Fetch sizes for all size ranges
  const fetchSizesForAllRanges = async (sizeRanges) => {
    try {
      const rangesWithSizes = await Promise.all(
        sizeRanges.map(async (range) => {
          const response = await fetch(
            `${BASE_URL}/api/admin/sizes/get-sizes?company_code=${company_code}&size_range_id=${range.size_range_id}`,
          );
          const data = await response.json();
          return {
            range,
            sizes: data.success ? data.sizes : [],
          };
        }),
      );
      setAllSizeRangesWithSizes(rangesWithSizes);
    } catch (err) {
      console.error("Error fetching sizes for ranges:", err);
    }
  };

  // Fetch style-specific attributes
  const fetchStyleAttributes = useCallback(async () => {
    try {
      const response = await fetch(
        `${BASE_URL}/api/admin/styles/get-style-attributes/${styleNumber}?company_code=${company_code}`,
      );
      const data = await response.json();
      if (data.success) {
        setStyleColors(data.colors || []);
        setStyleSizes(data.sizes || []);
        setStyleMaterials(data.materials || []);
        setStyleFits(data.fits || []);

        // For sizes, group by size_range_id
        if (data.sizes && data.sizes.length > 0) {
          const rangeMap = {};
          for (const sz of data.sizes) {
            if (!rangeMap[sz.size_range_id]) rangeMap[sz.size_range_id] = [];
            rangeMap[sz.size_range_id].push(sz);
          }
          setAssignedRangeSizes(rangeMap);
        } else {
          setAssignedRangeSizes({});
          setAssignedSizeRanges([]);
        }
      }
    } catch (err) {
      showError(err.message || "Error fetching style attributes", "Error");
    }
  }, [styleNumber, company_code, showError]);

  useEffect(() => {
    const loadAllData = async () => {
      setPageLoading(true);
      try {
        // Fetch company settings first so we know which attribute tabs to load
        await fetchCompanySettings();
        await Promise.all([
          fetchStyleDetails(),
          fetchAllAttributes(),
          fetchStyleAttributes(),
        ]);
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setPageLoading(false);
      }
    };

    loadAllData();
  }, [
    fetchStyleDetails,
    fetchAllAttributes,
    fetchStyleAttributes,
    fetchCompanySettings,
  ]);

  // Ensure active tab is a valid enabled tab; pick first enabled if current is disabled
  useEffect(() => {
    const order = ["colors", "sizes", "materials", "fits"];
    if (attributeToggles[activeTab] === false) {
      const first = order.find((k) => attributeToggles[k]);
      setActiveTab(first || "sku");
    }
  }, [attributeToggles, activeTab]);

  // Separate effect to build assignedSizeRanges when allSizeRanges and assignedRangeSizes are available
  useEffect(() => {
    if (
      allSizeRanges.length > 0 &&
      Object.keys(assignedRangeSizes).length > 0
    ) {
      const assignedRanges = Object.keys(assignedRangeSizes)
        .map((rangeId) => {
          const range = allSizeRanges.find(
            (r) => String(r.size_range_id) === String(rangeId),
          );
          return { range, sizes: assignedRangeSizes[rangeId] };
        })
        .filter((r) => r.range);
      setAssignedSizeRanges(assignedRanges);
    }
  }, [allSizeRanges, assignedRangeSizes]);

  // Function to refresh data after operations (doesn't affect page loading state)
  const refreshData = useCallback(async () => {
    try {
      await Promise.all([fetchAllAttributes(), fetchStyleAttributes()]);
    } catch (error) {
      console.error("Error refreshing data:", error);
    }
  }, [fetchAllAttributes, fetchStyleAttributes]);

  const handleOpenModal = (type) => {
    // Prevent opening the size-range modal if a size range is already assigned
    if (
      type === "sizes" &&
      assignedSizeRanges &&
      assignedSizeRanges.length > 0
    ) {
      showError(
        "Only one size range can be assigned to a style. Remove the existing range first to add another.",
        "Error",
      );
      return;
    }

    setModalType(type);
    setSelectedItems([]);
    setSelectedSizeRangeId(""); // Reset size range selection
    setShowModal(true);
  };

  const handleAttributeToggle = (itemId) => {
    setSelectedItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId],
    );
  };

  const handleSaveAttributes = async () => {
    if (selectedItems.length === 0) return;

    setLoading(true);
    try {
      // For sizes, assign size range(s) to style
      const response = await fetch(
        `${BASE_URL}/api/admin/styles/add-style-attributes`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            style_number: styleNumber,
            company_code,
            type: modalType,
            attribute_ids: selectedItems,
          }),
        },
      );

      const data = await response.json();
      if (data.success) {
        // showSuccess(`${modalType} added successfully!`, 'Success');
        setShowModal(false);
        refreshData();
      } else {
        showError(data.message || `Error adding ${modalType}`, "Error");
      }
    } catch (err) {
      showError(err.message || `Error adding ${modalType}`, "Error");
    }
    setLoading(false);
  };

  // Use DeleteModal for all attribute types
  const handleRemoveAttribute = (type, attributeId) => {
    setDeleteModalInfo({ type, id: attributeId });
  };

  // For sizes, only allow selecting a size range (not individual sizes)
  const getAvailableSizeRanges = () => {
    // Exclude already assigned ranges
    const assignedRangeIds = Object.keys(assignedRangeSizes || {});
    return allSizeRangesWithSizes.filter(
      ({ range }) => !assignedRangeIds.includes(String(range.size_range_id)),
    );
  };

  // Remove assigned size range from style
  const handleRemoveSizeRange = (size_range_id) => {
    setDeleteModalInfo({ type: "sizes", id: size_range_id });
  };

  // Size Guide Modal handlers
  const handleOpenSizeGuideModal = () => {
    setShowSizeGuideModal(true);
  };

  const handleCloseSizeGuideModal = () => {
    setShowSizeGuideModal(false);
  };

  const handleSizeGuideSuccess = (message) => {
    // showSuccess(message, 'Success');
  };

  // Measure Guide Modal handlers
  const handleOpenMeasureGuideModal = () => {
    setShowMeasureGuideModal(true);
  };

  const handleCloseMeasureGuideModal = () => {
    setShowMeasureGuideModal(false);
  };

  const handleMeasureGuideSuccess = (message) => {
    // showSuccess(message, 'Success');
  };

  // Render two-column layout: left = Add New, right = Add Existing (table + assign)
  const renderAttributeSection = (title, items, type) => (
    <Row className="mb-4">
      {/* Left: Add New */}
      <Col md={6}>
        <Card.Body>
          {type === "colors" && (
            <ColorManagement
              embedded
              styleNumber={styleNumber}
              companyCode={company_code}
              onSuccess={refreshData}
              onCancel={() => {}}
            />
          )}
          {type === "sizes" && (
            <SizeManagement
              embedded
              styleNumber={styleNumber}
              companyCode={company_code}
              onSuccess={refreshData}
              onCancel={() => {}}
            />
          )}
          {type === "materials" && (
            <MaterialManagement
              embedded
              styleNumber={styleNumber}
              companyCode={company_code}
              onSuccess={refreshData}
              onCancel={() => {}}
            />
          )}
          {type === "fits" && (
            <FitManagement
              embedded
              styleNumber={styleNumber}
              companyCode={company_code}
              onSuccess={refreshData}
              onCancel={() => {}}
            />
          )}
        </Card.Body>
      </Col>
      {/* Right: Assigned Size Ranges (Accordion) or Table for others */}
      <Col md={6}>
        {type === "sizes" && (
          <div className="d-flex justify-content-end mb-2 gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={handleOpenSizeGuideModal}
              className="add-size-btn"
              style={{ padding: "10px" }}
            >
              <FaPlus size={14} />
              Add Size Guide
            </Button>

            {/* add how to measure guide */}
            <Button
              variant="primary"
              size="sm"
              onClick={handleOpenMeasureGuideModal}
              className="add-measure-btn"
              style={{ padding: "10px" }}
            >
              <FaPlus size={14} />
              Add Measure Guide
            </Button>

            <Button
              variant="primary"
              className="add-style-btn"
              size="sm"
              onClick={() => handleOpenModal(type)}
              disabled={
                type === "sizes" &&
                assignedSizeRanges &&
                assignedSizeRanges.length > 0
              }
              title={
                type === "sizes" &&
                assignedSizeRanges &&
                assignedSizeRanges.length > 0
                  ? "A size range is already assigned to this product"
                  : ""
              }
              style={{ padding: "10px" }}
            >
              {/* Add Existing button for sizes is outside accordion */}
              <FaPlus size={14} />
              Add Existing Size Range
            </Button>
          </div>
        )}
        {type === "sizes" ? (
          <Accordion
            defaultActiveKey={
              assignedSizeRanges.length
                ? String(assignedSizeRanges[0]?.range?.size_range_id)
                : undefined
            }
          >
            {assignedSizeRanges.length === 0 ? (
              <Card>
                <Card.Body className="text-center text-muted">
                  No size ranges assigned yet.
                </Card.Body>
              </Card>
            ) : (
              assignedSizeRanges.map(({ range, sizes }) => (
                <Accordion.Item
                  eventKey={String(range.size_range_id)}
                  key={range.size_range_id}
                >
                  <Accordion.Header className="accordion-header">
                    <div className="d-flex justify-content-between align-items-center w-100">
                      <span>{range.range_name}</span>
                      <FaTrash
                        size={16}
                        className="action-icon text-danger ms-2"
                        style={{ cursor: "pointer" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveSizeRange(range.size_range_id);
                        }}
                        title="Remove Size Range"
                      />
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    <Table bordered size="sm" className="mb-0">
                      <thead>
                        <tr>
                          {/* Measure Guide Modal */}
                          <MeasureGuideModal
                            show={showMeasureGuideModal}
                            onHide={handleCloseMeasureGuideModal}
                            onSave={handleMeasureGuideSuccess}
                            title={`Add Measure Guide - ${
                              style?.name || "Loading..."
                            }`}
                            companyCode={company_code}
                            styleNumber={styleNumber}
                          />
                          <th>#</th>
                          <th>Size Name</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sizes.map((sz, idx) => (
                          <tr key={sz.size_id}>
                            <td>{idx + 1}</td>
                            <td>{sz.size_name}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Accordion.Body>
                </Accordion.Item>
              ))
            )}
          </Accordion>
        ) : (
          <Card className="assigned-attributes">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Assigned {title}</h5>
              <Button
                variant="primary"
                className="add-style-btn"
                size="sm"
                onClick={() => handleOpenModal(type)}
                style={{ padding: "10px" }}
              >
                <FaPlus size={14} />
                Add Existing {title.slice(0, -1)}
              </Button>
            </Card.Header>

            <Card.Body>
              {items.length === 0 ? (
                <p className="text-muted text-center">
                  No {title.toLowerCase()} added yet.
                </p>
              ) : (
                <Table responsive striped size="sm" className="table-organized">
                  <thead className="color-thead">
                    <tr style={{ verticalAlign: "middle" }}>
                      <th style={{ width: "40px" }}>#</th>
                      {type === "colors" && (
                        <th style={{ width: "20%" }}>Preview</th>
                      )}
                      <th style={{ minWidth: "120px" }}>Name</th>
                      {type === "colors" && (
                        <th style={{ minWidth: "40%" }}>Code</th>
                      )}
                      {(type === "materials" || type === "fits") && (
                        <th style={{ minWidth: "120px" }}>Description</th>
                      )}
                      <th style={{ width: "70px" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr
                        key={index}
                        style={{ verticalAlign: "middle", height: "36px" }}
                      >
                        <td>{index + 1}</td>
                        {type === "colors" && (
                          <td>
                            <span
                              style={{
                                display: "inline-block",
                                width: "100%",
                                height: 24,
                                border: "1px solid #ccc",
                                background: item.color_code || "#fff",
                                verticalAlign: "middle",
                              }}
                              title={item.color_code}
                            />
                          </td>
                        )}
                        <td
                          style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {type === "colors"
                            ? item.color_name
                            : type === "materials"
                              ? item.material_name
                              : item.fit_name}
                        </td>
                        {type === "colors" && (
                          <td style={{ fontFamily: "monospace" }}>
                            {item.color_code}
                          </td>
                        )}
                        {(type === "materials" || type === "fits") && (
                          <td>{item.description || "-"}</td>
                        )}
                        <td>
                          {type === "colors" ? (
                            <FaTrash
                              size={16}
                              className="action-icon text-danger"
                              style={{ cursor: "pointer" }}
                              onClick={() =>
                                handleRemoveAttribute(type, item.color_id)
                              }
                              title="Remove Color"
                            />
                          ) : (
                            <FaTrash
                              size={16}
                              className="action-icon text-danger"
                              style={{ cursor: "pointer" }}
                              onClick={() =>
                                handleRemoveAttribute(
                                  type,
                                  type === "materials"
                                    ? item.material_id
                                    : item.fit_id,
                                )
                              }
                              title={`Remove ${
                                type === "materials" ? "Material" : "Fit"
                              }`}
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        )}
      </Col>
    </Row>
  );

  return (
    <Container fluid className="manage-attributes-container">
      {/* Show spinner while page is loading */}
      {pageLoading ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "60vh" }}
        >
          <Spinner text="Loading product attributes..." />
        </div>
      ) : (
        <>
          {/* DeleteModal for all attribute types (must be outside renderAttributeSection) */}
          <DeleteModal
            id={deleteModalInfo.id}
            show={!!deleteModalInfo.id}
            onHide={() => setDeleteModalInfo({ type: "", id: null })}
            deleteUrl={() =>
              `${BASE_URL}/api/admin/styles/remove-style-attribute`
            }
            entityLabel={
              deleteModalInfo.type === "colors"
                ? "color from product"
                : deleteModalInfo.type === "sizes"
                  ? "size from product"
                  : deleteModalInfo.type === "materials"
                    ? "material from product"
                    : deleteModalInfo.type === "fits"
                      ? "fit from product"
                      : "attribute from product"
            }
            modalTitle={
              deleteModalInfo.type === "colors"
                ? "Remove Color from Product"
                : deleteModalInfo.type === "sizes"
                  ? "Remove Size from Product"
                  : deleteModalInfo.type === "materials"
                    ? "Remove Material from Product"
                    : deleteModalInfo.type === "fits"
                      ? "Remove Fit from Product"
                      : "Remove Attribute from Product"
            }
            confirmMessage={
              deleteModalInfo.type === "colors"
                ? "Are you sure you want to remove this color from the product?"
                : deleteModalInfo.type === "sizes"
                  ? "Are you sure you want to remove this size from the product?"
                  : deleteModalInfo.type === "materials"
                    ? "Are you sure you want to remove this material from the product?"
                    : deleteModalInfo.type === "fits"
                      ? "Are you sure you want to remove this fit from the product?"
                      : "Are you sure you want to remove this attribute from the product?"
            }
            requestOptions={{
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                style_number: styleNumber,
                company_code,
                type: deleteModalInfo.type,
                attribute_id: deleteModalInfo.id,
              }),
            }}
            onDeleteSuccess={() => {
              refreshData();
            }}
          />
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-2 p-4">
            <div>
              <Button
                variant="secondary"
                onClick={() => navigate("/products")}
                className="btn-div"
              >
                <FaArrowLeft size={16} />
                {/* Back */}
              </Button>
              <h2 className="d-inline">Manage Attributes</h2>
            </div>
            {style && (
              <Alert className="fs-6">
                {style?.name || "Loading..."} - {style.style_number}
              </Alert>
            )}
          </div>

          {/* Tabs for Attributes - only show tabs enabled by company settings */}
          <Tabs
            id="style-attributes-tabs"
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-4"
            justify
          >
            {attributeToggles.colors && (
              <Tab eventKey="colors" title="Colors">
                {renderAttributeSection("Colors", styleColors, "colors")}
              </Tab>
            )}

            {attributeToggles.sizes && (
              <Tab eventKey="sizes" title="Sizes">
                {renderAttributeSection("Sizes", styleSizes, "sizes")}
              </Tab>
            )}

            {attributeToggles.materials && (
              <Tab eventKey="materials" title="Materials">
                {renderAttributeSection(
                  "Materials",
                  styleMaterials,
                  "materials",
                )}
              </Tab>
            )}

            {attributeToggles.fits && (
              <Tab eventKey="fits" title="Fits">
                {renderAttributeSection("Fits", styleFits, "fits")}
              </Tab>
            )}

            {/* SKU tab - show when at least one attribute type is enabled */}
            {(attributeToggles.colors ||
              attributeToggles.sizes ||
              attributeToggles.materials ||
              attributeToggles.fits) && (
              <Tab eventKey="sku" title="SKU">
                <SkuVariantGenerator
                  style={style}
                  styleColors={styleColors}
                  styleSizes={styleSizes}
                  styleMaterials={styleMaterials}
                  styleFits={styleFits}
                  company_code={company_code}
                  styleNumber={styleNumber}
                  BASE_URL={BASE_URL}
                  onSuccess={() => {
                    showSuccess("Variants added successfully!", "Success");
                  }}
                  onError={(msg) => showError(msg, "Error")}
                  assignedRangeSizes={assignedRangeSizes}
                  showSuccessModal={showSuccessModal}
                  hideSuccess={hideSuccess}
                  successMessage={successMessage}
                  successTitle={successTitle}
                  showErrorModal={showErrorModal}
                  hideError={hideError}
                  errorMessage={errorMessage}
                  errorTitle={errorTitle}
                />
              </Tab>
            )}
          </Tabs>

          {/* Selection Modal */}
          <Modal
            show={showModal}
            onHide={() => setShowModal(false)}
            dialogClassName="selection-modal-dialog"
            size="l"
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>
                {modalType === "sizes"
                  ? "Add Size Range"
                  : `Add ${
                      modalType.charAt(0).toUpperCase() + modalType.slice(1, -1)
                    }`}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {modalType === "sizes" ? (
                <div>
                  <p>Select a size range to add to this product:</p>
                  {getAvailableSizeRanges().length === 0 ? (
                    <p className="text-muted text-center">
                      No available size ranges to add.
                    </p>
                  ) : (
                    <div className="table-responsive">
                      <Table striped bordered hover size="sm">
                        <thead className="color-thead">
                          <tr>
                            <th style={{ width: "40%" }}>Size Range Name</th>
                            <th>Sizes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getAvailableSizeRanges().map(({ range, sizes }) => (
                            <tr
                              key={range.size_range_id}
                              onClick={() =>
                                setSelectedSizeRangeId(range.size_range_id)
                              }
                              style={{ cursor: "pointer" }}
                              className={`size-range-row ${
                                selectedSizeRangeId === range.size_range_id
                                  ? "selected"
                                  : ""
                              }`}
                            >
                              <td>
                                <strong>{range.range_name}</strong>
                              </td>
                              <td>
                                {sizes.map((size, idx) => (
                                  <span
                                    key={size.size_id}
                                    bg="secondary"
                                    className="me-1 mb-1"
                                    style={{
                                      fontSize: "0.75rem",
                                      color: "#ffffff",
                                      backgroundColor: "#8083af",
                                      padding: "2px 6px",
                                      borderRadius: "4px",
                                      display: "inline-block",
                                    }}
                                  >
                                    {size.size_name}
                                  </span>
                                ))}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <p>Select {modalType} to add to this style:</p>
                  {/* <Table responsive striped size="sm" className="table-organized"> */}
                  <div className="table-responsive">
                    <Table striped size="sm" className="table-organized">
                      <thead className="color-thead">
                        <tr style={{ verticalAlign: "middle" }}>
                          <th style={{ width: "60px" }}>Select</th>
                          <th style={{ minWidth: "120px" }}>Name</th>
                          {modalType === "colors" && (
                            <th style={{ minWidth: "80px" }}>Code</th>
                          )}
                          {(modalType === "materials" ||
                            modalType === "fits") && (
                            <th style={{ minWidth: "120px" }}>Description</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          let allItems = [];
                          let styleItems = [];
                          switch (modalType) {
                            case "colors":
                              allItems = allColors;
                              styleItems = styleColors;
                              break;
                            case "materials":
                              allItems = allMaterials;
                              styleItems = styleMaterials;
                              break;
                            case "fits":
                              allItems = allFits;
                              styleItems = styleFits;
                              break;
                            default:
                              return null;
                          }
                          const styleItemIds = styleItems.map((item) => {
                            switch (modalType) {
                              case "colors":
                                return item.color_id;
                              case "materials":
                                return item.material_id;
                              case "fits":
                                return item.fit_id;
                              default:
                                return null;
                            }
                          });
                          return allItems
                            .filter((item) => {
                              const itemId =
                                modalType === "colors"
                                  ? item.color_id
                                  : modalType === "materials"
                                    ? item.material_id
                                    : item.fit_id;
                              return !styleItemIds.includes(itemId);
                            })
                            .map((item) => {
                              const itemId =
                                modalType === "colors"
                                  ? item.color_id
                                  : modalType === "materials"
                                    ? item.material_id
                                    : item.fit_id;
                              return (
                                <tr
                                  key={itemId}
                                  onClick={() => handleAttributeToggle(itemId)}
                                  style={{
                                    verticalAlign: "middle",
                                    height: "34px",
                                    cursor: "pointer",
                                  }}
                                  className={
                                    selectedItems.includes(itemId)
                                      ? "table-active"
                                      : ""
                                  }
                                >
                                  <td onClick={(e) => e.stopPropagation()}>
                                    <Form.Check
                                      type="checkbox"
                                      checked={selectedItems.includes(itemId)}
                                      onChange={() =>
                                        handleAttributeToggle(itemId)
                                      }
                                      style={{ marginTop: 0, marginBottom: 0 }}
                                    />
                                  </td>
                                  <td
                                    style={{
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {modalType === "colors"
                                      ? item.color_name
                                      : modalType === "materials"
                                        ? item.material_name
                                        : item.fit_name}
                                  </td>
                                  {modalType === "colors" && (
                                    <td style={{ fontFamily: "monospace" }}>
                                      {item.color_code}
                                    </td>
                                  )}
                                  {(modalType === "materials" ||
                                    modalType === "fits") && (
                                    <td>{item.description || "-"}</td>
                                  )}
                                </tr>
                              );
                            });
                        })()}
                      </tbody>
                    </Table>
                  </div>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={async () => {
                  if (modalType === "sizes") {
                    // Prevent adding another size range if one already exists (defense-in-depth)
                    if (assignedSizeRanges && assignedSizeRanges.length > 0) {
                      showError(
                        "Only one size range can be assigned to a product. Remove the existing range first to add another.",
                        "Error",
                      );
                      return;
                    }
                    if (!selectedSizeRangeId) return;
                    setLoading(true);
                    try {
                      // Assign only the size_range_id to style_size_ranges, ensure it's a number
                      const response = await fetch(
                        `${BASE_URL}/api/admin/styles/add-style-attributes`,
                        {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({
                            style_number: styleNumber,
                            company_code,
                            type: "sizes",
                            attribute_ids: [Number(selectedSizeRangeId)],
                          }),
                        },
                      );
                      const respData = await response.json();
                      if (respData.success) {
                        // showSuccess('Size range added successfully!', 'Success');
                        setShowModal(false);
                        refreshData();
                      } else {
                        showError(
                          respData.message || "Error adding size range",
                          "Error",
                        );
                      }
                    } catch (err) {
                      showError(
                        err.message || "Error adding size range",
                        "Error",
                      );
                    }
                    setLoading(false);
                  } else {
                    handleSaveAttributes();
                  }
                }}
                disabled={
                  loading ||
                  (modalType === "sizes" && !selectedSizeRangeId) ||
                  (modalType !== "sizes" && selectedItems.length === 0)
                }
              >
                {loading
                  ? "Adding..."
                  : modalType === "sizes"
                    ? "Add Size Range"
                    : `Add Selected ${modalType}`}
              </Button>
            </Modal.Footer>
          </Modal>

          {/* Size Guide Modal */}
          <SizeGuideModal
            isOpen={showSizeGuideModal}
            onClose={handleCloseSizeGuideModal}
            onSuccess={handleSizeGuideSuccess}
            title={`Size Guide - ${style?.name || "Loading..."}`}
            assignedRangeSizes={assignedRangeSizes}
            assignedSizeRanges={assignedSizeRanges}
            styleNumber={styleNumber}
            companyCode={company_code}
          />
        </>
      )}
    </Container>
  );
}

// Helper to generate cartesian product of arrays
function cartesianProduct(arrays) {
  return arrays.reduce(
    (a, b) => a.flatMap((d) => b.map((e) => [...d, e])),
    [[]],
  );
}

function SkuVariantGenerator({
  style,
  styleColors,
  styleSizes,
  styleMaterials,
  styleFits,
  company_code,
  styleNumber,
  BASE_URL,
  onSuccess,
  onError,
  assignedRangeSizes,
  showSuccessModal,
  hideSuccess,
  successMessage,
  successTitle,
  showErrorModal,
  hideError,
  errorMessage,
  errorTitle,
}) {
  const [existingVariants, setExistingVariants] = React.useState([]);
  // Filters for column-wise searching in the variants table
  const [variantFilters, setVariantFilters] = React.useState({
    sku: "",
    color: "",
    size: "",
    fit: "",
    material: "",
    unit_price: "",
    sale_price: "",
  });
  const [variantInputs, setVariantInputs] = React.useState({});
  const [editInputs, setEditInputs] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  // Apply-to-all fields
  const [applyAllUnitPrice, setApplyAllUnitPrice] = React.useState("");
  const [applyAllSalePrice, setApplyAllSalePrice] = React.useState("");
  // Selection state for which rows should receive apply-all values
  const [selectedExisting, setSelectedExisting] = React.useState({}); // {variant_id: true}
  const [selectedNew, setSelectedNew] = React.useState({}); // {index: true}

  // Determine which variant columns should be displayed
  const showColor =
    (Array.isArray(styleColors) && styleColors.length > 0) ||
    existingVariants.some((v) => v.color_id);
  const showSize =
    (Array.isArray(styleSizes) && styleSizes.length > 0) ||
    (assignedRangeSizes && Object.keys(assignedRangeSizes).length > 0) ||
    existingVariants.some((v) => v.size_id);
  const showFit =
    (Array.isArray(styleFits) && styleFits.length > 0) ||
    existingVariants.some((v) => v.fit_id);
  const showMaterial =
    (Array.isArray(styleMaterials) && styleMaterials.length > 0) ||
    existingVariants.some((v) => v.material_id);

  const dynamicCols =
    (showColor ? 1 : 0) +
    (showSize ? 1 : 0) +
    (showFit ? 1 : 0) +
    (showMaterial ? 1 : 0);
  const totalColumns = 2 + dynamicCols + 3;

  // Fetch existing variants on mount or when styleNumber changes
  React.useEffect(() => {
    if (!styleNumber) return;
    setLoading(true);
    fetch(
      `${BASE_URL}/api/admin/styles/get-style-variants/${styleNumber}?company_code=${company_code}`,
    )
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setExistingVariants(data.variants || []);
          // Initialize editInputs with current prices
          const editObj = {};
          (data.variants || []).forEach((v) => {
            editObj[v.variant_id] = {
              unit_price: v.unit_price,
              sale_price: v.sale_price,
            };
          });
          setEditInputs(editObj);
        } else {
          setError(data.message || "Failed to fetch variants");
        }
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to fetch variants");
        setLoading(false);
      });
  }, [BASE_URL, styleNumber, company_code]);

  // Generate all possible combinations
  const combinations = React.useMemo(() => {
    // Helper to deduplicate by key
    const uniqBy = (arr, key) => {
      const seen = new Set();
      return (arr || []).filter((item) => {
        const k = item && (item[key] ?? JSON.stringify(item));
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
    };

    // Fall back to existing variants for missing attribute lists
    const colorsArr =
      Array.isArray(styleColors) && styleColors.length
        ? styleColors
        : uniqBy(
            (existingVariants || []).map((v) => ({
              color_id: v.color_id,
              color_name: v.color_name,
            })),
            "color_id",
          );

    let sizesArr = [];
    if (Array.isArray(styleSizes) && styleSizes.length > 0) {
      sizesArr = styleSizes;
    } else if (
      typeof assignedRangeSizes === "object" &&
      Object.keys(assignedRangeSizes).length > 0
    ) {
      sizesArr = Object.values(assignedRangeSizes).flat();
    } else if (
      Array.isArray(window.assignedSizeRanges) &&
      window.assignedSizeRanges.length > 0
    ) {
      sizesArr = window.assignedSizeRanges.flatMap((r) => r.sizes);
    } else {
      sizesArr = uniqBy(
        (existingVariants || []).map((v) => ({
          size_id: v.size_id,
          size_name: v.size_name,
        })),
        "size_id",
      );
    }

    const fitsArr =
      Array.isArray(styleFits) && styleFits.length
        ? styleFits
        : uniqBy(
            (existingVariants || []).map((v) => ({
              fit_id: v.fit_id,
              fit_name: v.fit_name,
            })),
            "fit_id",
          );

    const materialsArr =
      Array.isArray(styleMaterials) && styleMaterials.length
        ? styleMaterials
        : uniqBy(
            (existingVariants || []).map((v) => ({
              material_id: v.material_id,
              material_name: v.material_name,
            })),
            "material_id",
          );

    // Ensure we always have at least one entry per axis (use placeholders when truly missing)
    const placeholder = (shape) => [shape];
    const finalColors = colorsArr.length
      ? colorsArr
      : placeholder({ color_id: null, color_name: "" });
    const finalSizes = sizesArr.length
      ? sizesArr
      : placeholder({ size_id: null, size_name: "" });
    const finalFits = fitsArr.length
      ? fitsArr
      : placeholder({ fit_id: null, fit_name: "" });
    const finalMaterials = materialsArr.length
      ? materialsArr
      : placeholder({ material_id: null, material_name: "" });

    return cartesianProduct([
      finalColors,
      finalSizes,
      finalFits,
      finalMaterials,
    ]);
  }, [
    styleColors,
    styleFits,
    assignedRangeSizes,
    styleSizes,
    styleMaterials,
    existingVariants,
  ]);

  // Generate SKU string (same as backend logic, now includes material)
  // Build SKU by joining only non-empty segments to avoid consecutive dashes
  function generateSku(style_number, color, size, fit, material) {
    const parts = [];
    const safe = (v) => (v === null || v === undefined ? "" : String(v).trim());

    const sn = safe(style_number);
    if (sn) parts.push(sn);

    const cn = safe(color && color.color_name);
    if (cn) parts.push(cn.substring(0, 3).toUpperCase());

    const sz = safe(size && size.size_name);
    if (sz) parts.push(sz.replace(/\s+/g, "").toUpperCase());

    const fn = safe(fit && fit.fit_name);
    if (fn) parts.push(fn.substring(0, 3).toUpperCase());

    const mn = safe(material && material.material_name);
    if (mn) parts.push(mn.substring(0, 3).toUpperCase());

    return parts.join("-");
  }

  // Map of existing SKUs for quick lookup
  const existingSkuMap = React.useMemo(() => {
    const map = {};
    existingVariants.forEach((v) => {
      map[v.sku] = v;
    });
    return map;
  }, [existingVariants]);

  // Only show new combinations (not already in DB) for adding
  const newCombinations = React.useMemo(() => {
    if (!style) return []; // to avoid runtime err

    return combinations.filter((combo) => {
      const sku = generateSku(
        style.style_number,
        combo[0],
        combo[1],
        combo[2],
        combo[3],
      );
      return !existingSkuMap[sku];
    });
  }, [combinations, existingSkuMap, style]);

  // Handle input change for new variants
  const handleInputChange = (idx, field, value) => {
    setVariantInputs((inputs) => ({
      ...inputs,
      [idx]: {
        ...inputs[idx],
        [field]: value,
      },
    }));
  };

  // Propagate apply-to-all values into both new and existing variant inputs
  const propagateToAll = React.useCallback(
    (unitValue, saleValue) => {
      // Update only selected new variant inputs
      setVariantInputs((inputs) => {
        const newInputs = { ...inputs };
        newCombinations.forEach((_, idx) => {
          if (selectedNew[idx]) {
            newInputs[idx] = {
              ...newInputs[idx],
              unit_price: unitValue,
              sale_price: saleValue,
            };
          }
        });
        return newInputs;
      });

      // Update only selected existing variant edit inputs
      setEditInputs((inputs) => {
        const newEdit = { ...inputs };
        existingVariants.forEach((v) => {
          if (selectedExisting[v.variant_id]) {
            newEdit[v.variant_id] = {
              ...newEdit[v.variant_id],
              unit_price: unitValue,
              sale_price: saleValue,
            };
          }
        });
        return newEdit;
      });
    },
    [newCombinations, existingVariants, selectedNew, selectedExisting],
  );

  // Toggle handlers for selection checkboxes
  const toggleSelectExisting = (variant_id) => {
    setSelectedExisting((s) => ({ ...s, [variant_id]: !s[variant_id] }));
  };

  const toggleSelectNew = (idx) => {
    setSelectedNew((s) => ({ ...s, [idx]: !s[idx] }));
  };

  // Master select-all state and handler (selects all visible rows)
  const allSelected = React.useMemo(() => {
    const existingCount = existingVariants.length;
    const newCount = newCombinations.length;
    if (existingCount + newCount === 0) return false;
    const allExistingSelected = existingVariants.every(
      (v) => !!selectedExisting[v.variant_id],
    );
    const allNewSelected = newCombinations.every(
      (_, idx) => !!selectedNew[idx],
    );
    return allExistingSelected && allNewSelected;
  }, [existingVariants, newCombinations, selectedExisting, selectedNew]);

  // Helper to test substring match (case-insensitive)
  const matchesFilter = (value, filter) => {
    if (!filter) return true;
    return String(value ?? "")
      .toLowerCase()
      .includes(String(filter).toLowerCase());
  };

  // Filtered lists used for rendering table rows
  const filteredExistingVariants = React.useMemo(() => {
    return existingVariants.filter(
      (v) =>
        matchesFilter(v.sku, variantFilters.sku) &&
        matchesFilter(v.color_name, variantFilters.color) &&
        matchesFilter(v.size_name, variantFilters.size) &&
        matchesFilter(v.fit_name, variantFilters.fit) &&
        matchesFilter(v.material_name, variantFilters.material) &&
        matchesFilter(v.unit_price, variantFilters.unit_price) &&
        matchesFilter(v.sale_price, variantFilters.sale_price),
    );
  }, [existingVariants, variantFilters]);

  // Build displayed new combinations preserving original index so selection maps correctly
  const filteredNewCombinations = React.useMemo(() => {
    return newCombinations
      .map((combo, idx) => ({ combo, idx }))
      .filter(({ combo, idx }) => {
        const [color, size, fit, material] = combo;
        const sku = generateSku(
          style?.style_number || styleNumber,
          color,
          size,
          fit,
          material,
        );
        const matches =
          matchesFilter(sku, variantFilters.sku) &&
          matchesFilter(color?.color_name, variantFilters.color) &&
          matchesFilter(size?.size_name, variantFilters.size) &&
          matchesFilter(fit?.fit_name, variantFilters.fit) &&
          matchesFilter(material?.material_name, variantFilters.material);
        return matches;
      });
  }, [newCombinations, variantFilters, style, styleNumber]);

  const hasSelection = React.useMemo(() => {
    return (
      Object.values(selectedExisting || {}).some(Boolean) ||
      Object.keys(selectedNew || {}).some((k) => !!selectedNew[k])
    );
  }, [selectedExisting, selectedNew]);

  const displayedExistingVariants = React.useMemo(() => {
    if (!hasSelection) return filteredExistingVariants;
    return filteredExistingVariants.filter(
      (v) => !!selectedExisting[v.variant_id],
    );
  }, [filteredExistingVariants, selectedExisting, hasSelection]);

  const displayedNewCombinations = React.useMemo(() => {
    if (!hasSelection) return filteredNewCombinations;
    return filteredNewCombinations.filter(({ idx }) => !!selectedNew[idx]);
  }, [filteredNewCombinations, selectedNew, hasSelection]);

  const clearVariantFilters = () =>
    setVariantFilters({
      sku: "",
      color: "",
      size: "",
      fit: "",
      material: "",
      unit_price: "",
      sale_price: "",
    });

  const toggleSelectAll = () => {
    const select = !allSelected;
    // set existing
    const newExisting = {};
    existingVariants.forEach((v) => {
      newExisting[v.variant_id] = select;
    });
    setSelectedExisting(newExisting);
    // set new
    const newNew = {};
    newCombinations.forEach((_, idx) => {
      newNew[idx] = select;
    });
    setSelectedNew(newNew);
  };

  // Save all new variants
  const handleSaveAll = async () => {
    setSaving(true);
    setError("");
    try {
      // Save only selected new variants
      const selectedNewIndices = Object.keys(selectedNew)
        .filter((k) => selectedNew[k])
        .map((k) => Number(k));
      for (let i = 0; i < selectedNewIndices.length; i++) {
        const idx = selectedNewIndices[i];
        const combo = newCombinations[idx];
        if (!combo) continue;
        const [color, size, fit, material] = combo;
        const input = variantInputs[idx] || {};
        const payload = {
          company_code,
          style_number: style.style_number,
          sku: generateSku(style.style_number, color, size, fit, material),
          color_id: color.color_id,
          size_id: size.size_id,
          fit_id: fit.fit_id,
          material_id: material.material_id,
          unit_price: input.unit_price || 0,
          sale_price: input.sale_price || 0,
        };
        const res = await fetch(
          `${BASE_URL}/api/admin/styles/add-style-variants`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          },
        );
        const data = await res.json();
        if (!data.success) {
          if (
            data.message &&
            data.message.toLowerCase().includes("variant already exists")
          ) {
            setError(
              "One or more variants already exist. Please refresh the page.",
            );
          } else {
            setError(data.message || "Failed to add variant");
          }
          setSaving(false);
          return;
        }
      }

      // Update only selected existing variants
      const selectedExistingIds = Object.keys(selectedExisting).filter(
        (k) => selectedExisting[k],
      );
      for (let i = 0; i < selectedExistingIds.length; i++) {
        const variant_id = selectedExistingIds[i];
        const variant = existingVariants.find(
          (v) => String(v.variant_id) === String(variant_id),
        );
        if (!variant) continue;
        const input = editInputs[variant.variant_id] || {};
        const payload = {
          color_id: variant.color_id,
          size_id: variant.size_id,
          fit_id: variant.fit_id,
          material_id: variant.material_id,
          unit_price: input.unit_price,
          sale_price: input.sale_price,
          sku: variant.sku,
        };
        const res = await fetch(
          `${BASE_URL}/api/admin/styles/update-style-variants/${variant.variant_id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          },
        );
        const data = await res.json();
        if (!data.success) {
          setError(data.message || "Failed to update variant");
          setSaving(false);
          return;
        }
      }

      // Clear inputs for saved new variants
      setVariantInputs((inputs) => {
        const newInputs = { ...inputs };
        Object.keys(selectedNew).forEach((k) => {
          if (selectedNew[k]) delete newInputs[k];
        });
        return newInputs;
      });

      // Optionally clear selected flags for saved rows
      setSelectedNew({});
      setSelectedExisting({});

      if (onSuccess) onSuccess();

      // Refetch variants to refresh state
      setLoading(true);
      fetch(
        `${BASE_URL}/api/admin/styles/get-style-variants/${styleNumber}?company_code=${company_code}`,
      )
        .then((res) => res.json())
        .then((data) => {
          if (data.success) {
            setExistingVariants(data.variants || []);
            // Update editInputs as well
            const editObj = {};
            (data.variants || []).forEach((v) => {
              editObj[v.variant_id] = {
                unit_price: v.unit_price,
                sale_price: v.sale_price,
              };
            });
            setEditInputs(editObj);
          }
          setLoading(false);
        });
    } catch (err) {
      setError("Failed to save variants");
      setSaving(false);
      return;
    }
    setSaving(false);
  };

  // Save a single new variant by index
  const handleSaveSingle = async (idx) => {
    setSaving(true);
    setError("");
    try {
      const combo = newCombinations[idx];
      const [color, size, fit, material] = combo;
      const input = variantInputs[idx] || {};
      const payload = {
        company_code,
        style_number: style.style_number,
        sku: generateSku(style.style_number, color, size, fit, material),
        color_id: color.color_id,
        size_id: size.size_id,
        fit_id: fit.fit_id,
        material_id: material.material_id,
        unit_price: input.unit_price || 0,
        sale_price: input.sale_price || 0,
      };
      const res = await fetch(
        `${BASE_URL}/api/admin/styles/add-style-variants`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
      const data = await res.json();
      if (!data.success) {
        if (
          data.message &&
          data.message.toLowerCase().includes("variant already exists")
        ) {
          setError("Variant already exists. Please refresh the page.");
        } else {
          setError(data.message || "Failed to add variant");
        }
        setSaving(false);
        return;
      }
      // Remove the input for this row
      setVariantInputs((inputs) => {
        const newInputs = { ...inputs };
        delete newInputs[idx];
        return newInputs;
      });
      if (onSuccess) onSuccess();
      // Refetch variants
      setLoading(true);
      fetch(
        `${BASE_URL}/api/admin/styles/get-style-variants/${styleNumber}?company_code=${company_code}`,
      )
        .then((res) => res.json())
        .then((data) => {
          if (data.success) {
            setExistingVariants(data.variants || []);
            // Update editInputs as well
            const editObj = {};
            (data.variants || []).forEach((v) => {
              editObj[v.variant_id] = {
                unit_price: v.unit_price,
                sale_price: v.sale_price,
              };
            });
            setEditInputs(editObj);
          }
          setLoading(false);
        });
    } catch (err) {
      setError("Failed to save variant");
      setSaving(false);
      return;
    }
    setSaving(false);
  };

  // Handle input change for existing variants
  const handleEditInputChange = (variant_id, field, value) => {
    setEditInputs((inputs) => ({
      ...inputs,
      [variant_id]: {
        ...inputs[variant_id],
        [field]: value,
      },
    }));
  };

  // Save price update for a single existing variant
  const handleSaveEdit = async (variant) => {
    setSaving(true);
    setError("");
    try {
      const input = editInputs[variant.variant_id] || {};
      const payload = {
        color_id: variant.color_id,
        size_id: variant.size_id,
        fit_id: variant.fit_id,
        material_id: variant.material_id,
        unit_price: input.unit_price,
        sale_price: input.sale_price,
        sku: variant.sku,
      };
      const res = await fetch(
        `${BASE_URL}/api/admin/styles/update-style-variants/${variant.variant_id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
      const data = await res.json();
      if (!data.success) {
        setError(data.message || "Failed to update variant");
        setSaving(false);
        return;
      }
      if (onSuccess) onSuccess();
      // Refetch variants
      setLoading(true);
      fetch(
        `${BASE_URL}/api/admin/styles/get-style-variants/${styleNumber}?company_code=${company_code}`,
      )
        .then((res) => res.json())
        .then((data) => {
          if (data.success) {
            setExistingVariants(data.variants || []);
            // Update editInputs as well
            const editObj = {};
            (data.variants || []).forEach((v) => {
              editObj[v.variant_id] = {
                unit_price: v.unit_price,
                sale_price: v.sale_price,
              };
            });
            setEditInputs(editObj);
          }
          setLoading(false);
        });
    } catch (err) {
      setError("Failed to update variant");
      setSaving(false);
      return;
    }
    setSaving(false);
  };

  if (!style) return <div className="p-4">Loading products...</div>;
  if (loading) return <div className="p-4">Loading variants...</div>;

  return (
    <div className="p-3">
      <h4>Product Variants (SKU)</h4>
      {error && <div className="alert alert-danger">{error}</div>}

      <Container fluid className="add-all-variants">
        <h5> Apply to All Variants </h5>
        <Row className="mb-3">
          <Col>
            <Form.Label>Unit Price</Form.Label>
            <Form.Control
              type="number"
              min="0"
              placeholder="Enter unit price for all SKUs"
              value={applyAllUnitPrice}
              onChange={(e) => {
                const val = e.target.value;
                setApplyAllUnitPrice(val);
                // propagate to selected rows immediately
                propagateToAll(val, applyAllSalePrice);
              }}
            />
          </Col>
          <Col>
            <Form.Label>Sale Price</Form.Label>
            <Form.Control
              type="number"
              min="0"
              placeholder="Enter sale price for all SKUs"
              value={applyAllSalePrice}
              onChange={(e) => {
                const val = e.target.value;
                setApplyAllSalePrice(val);
                // propagate to selected rows immediately
                propagateToAll(applyAllUnitPrice, val);
              }}
            />
          </Col>
          <Col>
            <Button
              variant="primary"
              className="var-add-all-btn"
              onClick={handleSaveAll}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Selected Variants"}
            </Button>
          </Col>
        </Row>
      </Container>

      <Table responsive bordered size="sm" className="variants-table mt-3">
        <thead>
          <tr>
            <th style={{ width: "40px" }}>
              <Form.Check
                type="checkbox"
                checked={!!allSelected}
                onChange={toggleSelectAll}
                aria-label="select-all-variants"
              />
            </th>
            <th>#</th>
            <th>SKU</th>
            {showColor && <th>Color</th>}
            {showSize && <th>Size</th>}
            {showFit && <th>Fit</th>}
            {showMaterial && <th>Material</th>}
            <th>Unit Price</th>
            <th>Sale Price</th>
            <th>Action</th>
          </tr>
          {/* Filters row - per-column search inputs */}
          <tr>
            <th>
              <Button
                variant="outline"
                className="pf-clear-filters-btn"
                onClick={clearVariantFilters}
                title="Clear filters"
              >
                <FaEraser />
              </Button>
            </th>
            <th></th>
            <th className="filters-row">
              <Form.Control
                size="sm"
                placeholder="Search SKU"
                value={variantFilters.sku}
                onChange={(e) =>
                  setVariantFilters((prev) => ({
                    ...prev,
                    sku: e.target.value,
                  }))
                }
              />
            </th>
            {showColor && (
              <th className="filters-row">
                <Form.Control
                  size="sm"
                  placeholder="Search Color"
                  value={variantFilters.color}
                  onChange={(e) =>
                    setVariantFilters((prev) => ({
                      ...prev,
                      color: e.target.value,
                    }))
                  }
                />
              </th>
            )}
            {showSize && (
              <th className="filters-row">
                <Form.Control
                  size="sm"
                  placeholder="Search Size"
                  value={variantFilters.size}
                  onChange={(e) =>
                    setVariantFilters((prev) => ({
                      ...prev,
                      size: e.target.value,
                    }))
                  }
                />
              </th>
            )}
            {showFit && (
              <th className="filters-row">
                <Form.Control
                  size="sm"
                  placeholder="Search Fit"
                  value={variantFilters.fit}
                  onChange={(e) =>
                    setVariantFilters((prev) => ({
                      ...prev,
                      fit: e.target.value,
                    }))
                  }
                />
              </th>
            )}
            {showMaterial && (
              <th className="filters-row">
                <Form.Control
                  size="sm"
                  placeholder="Search Material"
                  value={variantFilters.material}
                  onChange={(e) =>
                    setVariantFilters((prev) => ({
                      ...prev,
                      material: e.target.value,
                    }))
                  }
                />
              </th>
            )}
            <th className="filters-row">
              <Form.Control
                size="sm"
                placeholder="Unit"
                value={variantFilters.unit_price}
                onChange={(e) =>
                  setVariantFilters((prev) => ({
                    ...prev,
                    unit_price: e.target.value,
                  }))
                }
              />
            </th>
            <th className="filters-row">
              <Form.Control
                size="sm"
                placeholder="Sale"
                value={variantFilters.sale_price}
                onChange={(e) =>
                  setVariantFilters((prev) => ({
                    ...prev,
                    sale_price: e.target.value,
                  }))
                }
              />
            </th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {/* Existing variants */}
          {displayedExistingVariants.map((variant, idx) => {
            const input = editInputs[variant.variant_id] || {};
            return (
              <tr
                key={variant.variant_id || variant.sku}
                style={{ background: "#f8f9fa" }}
              >
                <td style={{ verticalAlign: "middle" }}>
                  <Form.Check
                    type="checkbox"
                    checked={!!selectedExisting[variant.variant_id]}
                    onChange={() => toggleSelectExisting(variant.variant_id)}
                    aria-label={`select-variant-${variant.variant_id}`}
                  />
                </td>
                <td>{idx + 1}</td>
                <td>{variant.sku}</td>
                {showColor && <td>{variant.color_name}</td>}
                {showSize && <td>{variant.size_name}</td>}
                {showFit && <td>{variant.fit_name}</td>}
                {showMaterial && <td>{variant.material_name}</td>}
                <td>
                  <input
                    type="number"
                    min="0"
                    className="form-control form-control-sm"
                    value={input.unit_price ?? ""}
                    onChange={(e) =>
                      handleEditInputChange(
                        variant.variant_id,
                        "unit_price",
                        e.target.value,
                      )
                    }
                    placeholder="Unit Price"
                  />
                </td>
                <td>
                  <input
                    type="number"
                    min="0"
                    className="form-control form-control-sm"
                    value={input.sale_price ?? ""}
                    onChange={(e) =>
                      handleEditInputChange(
                        variant.variant_id,
                        "sale_price",
                        e.target.value,
                      )
                    }
                    placeholder="Sale Price"
                  />
                </td>
                <td>
                  <FaSave
                    size={16}
                    className="action-icon text-success"
                    style={{ cursor: "pointer" }}
                    onClick={() => handleSaveEdit(variant)}
                    title="Save Changes"
                  />
                </td>
              </tr>
            );
          })}
          {/* New variants to be added */}
          {displayedNewCombinations.length > 0 && (
            <tr>
              <td
                colSpan={totalColumns}
                className="table-secondary text-center"
              >
                Add New Variants
              </td>
            </tr>
          )}
          {displayedNewCombinations.map(
            ({ combo, idx: originalIdx }, mapIdx) => {
              const [color, size, fit, material] = combo;
              const sku = generateSku(
                style.style_number,
                color,
                size,
                fit,
                material,
              );
              const input = variantInputs[originalIdx] || {};
              return (
                <tr key={sku} style={{ background: "#fffbe6" }}>
                  <td style={{ verticalAlign: "middle" }}>
                    <Form.Check
                      type="checkbox"
                      checked={!!selectedNew[originalIdx]}
                      onChange={() => toggleSelectNew(originalIdx)}
                      aria-label={`select-new-${originalIdx}`}
                    />
                  </td>
                  <td>{displayedExistingVariants.length + mapIdx + 1}</td>
                  <td>{sku}</td>
                  {showColor && <td>{color.color_name}</td>}
                  {showSize && <td>{size.size_name}</td>}
                  {showFit && <td>{fit.fit_name}</td>}
                  {showMaterial && <td>{material.material_name}</td>}
                  <td>
                    <input
                      type="number"
                      min="0"
                      className="form-control form-control-sm"
                      value={input.unit_price || ""}
                      onChange={(e) =>
                        handleInputChange(
                          originalIdx,
                          "unit_price",
                          e.target.value,
                        )
                      }
                      placeholder="Unit Price"
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      className="form-control form-control-sm"
                      value={input.sale_price || ""}
                      onChange={(e) =>
                        handleInputChange(
                          originalIdx,
                          "sale_price",
                          e.target.value,
                        )
                      }
                      placeholder="Sale Price"
                    />
                  </td>
                  <td>
                    <FaSave
                      size={16}
                      className="action-icon text-success"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleSaveSingle(originalIdx)}
                      title="Save Variant"
                    />
                  </td>
                </tr>
              );
            },
          )}
        </tbody>
      </Table>
      {/* {newCombinations.length > 0 && (
        <Button
          variant="primary"
          className="mt-2"
          onClick={handleSaveAll}
          disabled={saving || newCombinations.length === 0}
        >
          {saving ? 'Saving...' : 'Save All New Variants'}
        </Button>
      )} */}

      {/* Success Modal */}
      <SuccessModal
        show={showSuccessModal}
        onHide={hideSuccess}
        message={successMessage}
        title={successTitle}
        variant="success"
      />

      {/* Error Modal */}
      <SuccessModal
        show={showErrorModal}
        onHide={hideError}
        message={errorMessage}
        title={errorTitle}
        variant="error"
      />
    </div>
  );
}
